import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tournamentApi } from '../services/api';

const ALL_SPORTS = [
  { value: 'badminton', label: 'Badminton' },
  { value: 'table_tennis', label: 'Table Tennis' },
  { value: 'tennis', label: 'Tennis' },
  { value: 'padel', label: 'Padel' },
  { value: 'squash', label: 'Squash' },
  { value: 'pickleball', label: 'Pickleball' }
];

const FormSection = ({ title, children }) => (
  <div style={{ marginBottom: '1.75rem' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>{title}</span>
      <div style={{ flex: 1, height: 1, background: '#f0f0f0' }} />
    </div>
    {children}
  </div>
);

const CreateTournament = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sport_type: 'badminton',
    venue: '',
    start_date: '',
    end_date: '',
    registration_deadline: '',
    entry_fee: 0,
    prize_money: '',
    status: 'draft',
    online_registration_enabled: true,
    registration_requires_approval: false,
    registration_contact_name: '',
    registration_contact_phone: '',
    registration_contact_email: '',
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let parsed = value;
    if (type === 'number') {
      const num = parseFloat(value);
      parsed = isNaN(num) ? 0 : num;
    } else if (type === 'checkbox') {
      parsed = checked;
    }
    setFormData(prev => ({ ...prev, [name]: parsed }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await tournamentApi.create(formData);
      navigate(`/tournaments/${response.data.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create tournament');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="page-header">
        <h1 className="page-title">Create Tournament</h1>
        <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: 4 }}>
          Set up the tournament details. Add events and entries after creation.
        </p>
      </div>
      <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleSubmit}>

          <FormSection title="Basic Info">
            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Tournament Name *</label>
                <input type="text" name="name" className="form-input" value={formData.name}
                  onChange={handleChange} required placeholder="Enter tournament name" />
              </div>
              <div className="form-group">
                <label className="form-label">Sport *</label>
                <select name="sport_type" className="form-select" value={formData.sport_type}
                  onChange={handleChange} required>
                  {ALL_SPORTS.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Venue</label>
              <input type="text" name="venue" className="form-input" value={formData.venue}
                onChange={handleChange} placeholder="e.g., City Sports Complex" />
            </div>
          </FormSection>

          <FormSection title="Schedule">
            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Start Date *</label>
                <input type="date" name="start_date" className="form-input" value={formData.start_date}
                  onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input type="date" name="end_date" className="form-input" value={formData.end_date}
                  onChange={handleChange} />
              </div>
            </div>
            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Registration Deadline</label>
                <input type="datetime-local" name="registration_deadline" className="form-input"
                  value={formData.registration_deadline} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select name="status" className="form-select" value={formData.status} onChange={handleChange}>
                  <option value="draft">Draft</option>
                  <option value="registration">Open for Registration</option>
                </select>
              </div>
            </div>
          </FormSection>

          <FormSection title="Financials">
            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Entry Fee (default per event)</label>
                <input type="number" name="entry_fee" className="form-input" value={formData.entry_fee}
                  onChange={handleChange} min="0" step="0.01" />
              </div>
              <div className="form-group">
                <label className="form-label">Prize Money</label>
                <input type="text" name="prize_money" className="form-input" value={formData.prize_money}
                  onChange={handleChange} placeholder="e.g., $500 winner prize" />
              </div>
            </div>
          </FormSection>

          <FormSection title="Registration Settings">
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  name="online_registration_enabled"
                  checked={formData.online_registration_enabled}
                  onChange={handleChange}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                <span className="form-label" style={{ margin: 0 }}>Allow online self-registration</span>
              </label>
              <p style={{ margin: '0.3rem 0 0 1.6rem', fontSize: '0.78rem', color: '#9ca3af' }}>
                {formData.online_registration_enabled
                  ? 'Players can register directly via the public tournament page.'
                  : 'Registration is closed — players must contact you directly to enter.'}
              </p>
            </div>
            {formData.online_registration_enabled && (
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    name="registration_requires_approval"
                    checked={formData.registration_requires_approval}
                    onChange={handleChange}
                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                  />
                  <span className="form-label" style={{ margin: 0 }}>Require approval before confirming registration</span>
                </label>
                <p style={{ margin: '0.3rem 0 0 1.6rem', fontSize: '0.78rem', color: '#9ca3af' }}>
                  {formData.registration_requires_approval
                    ? 'Players will be placed in a pending state until you approve each entry.'
                    : 'Players are registered immediately upon submission.'}
                </p>
              </div>
            )}
            <div className="grid grid-2" style={{ marginTop: '0.75rem' }}>
              <div className="form-group">
                <label className="form-label">Contact Person Name</label>
                <input type="text" name="registration_contact_name" className="form-input"
                  value={formData.registration_contact_name} onChange={handleChange}
                  placeholder="e.g. John Smith" />
              </div>
              <div className="form-group">
                <label className="form-label">Contact Phone</label>
                <input type="tel" name="registration_contact_phone" className="form-input"
                  value={formData.registration_contact_phone} onChange={handleChange}
                  placeholder="+91 XXXXX XXXXX" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Contact Email</label>
              <input type="email" name="registration_contact_email" className="form-input"
                value={formData.registration_contact_email} onChange={handleChange}
                placeholder="registrations@example.com" />
            </div>
          </FormSection>

          <FormSection title="Description">
            <div className="form-group">
              <textarea name="description" className="form-textarea" value={formData.description}
                onChange={handleChange} placeholder="Enter tournament description" rows="3" />
            </div>
          </FormSection>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', paddingTop: '0.5rem' }}>
            <button type="button" className="btn btn-outline" onClick={() => navigate('/tournaments')}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Tournament'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTournament;
